import { type FC, type ReactNode } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import Image from 'dibs-icons/exports/legacy/Image';
import { addParams } from 'dibs-crypto-utils/exports/imageOptimizationParams';
import CircleCheck from 'dibs-icons/exports/legacy/CircleCheck';
import { Spinner } from 'dibs-elements/exports/Spinner';
import { getEditionFromNftName } from '../../helpers/getEditionFromNftName';
import { type HeaderWithImage_draft$key } from './__generated__/HeaderWithImage_draft.graphql';
import { type HeaderWithImage_nftItem$key } from './__generated__/HeaderWithImage_nftItem.graphql';

import styles from './HeaderWithImage.scss';

type Props = {
    nftItemDraft?: HeaderWithImage_draft$key | null | undefined;
    nftItem?: HeaderWithImage_nftItem$key | null | undefined;
    status?: null | 'loading' | 'success' | 'error';
    title: string | ReactNode;
};

export const HeaderWithImage: FC<Props> = ({
    nftItemDraft: nftItemDraftRef = null,
    nftItem: nftItemRef = null,
    status = null,
    title,
}) => {
    const nftItem = useFragment(
        graphql`
            fragment HeaderWithImage_nftItem on NftItem {
                creatorAddress {
                    publicProfile {
                        profileName
                    }
                }
                metadata {
                    ... on NftDigitalArtMetadata {
                        name
                        edition
                        media {
                            webUrl
                            posterFrameUrl
                        }
                    }
                }
                totalSupply
                isERC1155
            }
        `,
        nftItemRef
    );
    const nftItemDraft = useFragment(
        graphql`
            fragment HeaderWithImage_draft on NftItemDraft {
                name
                user {
                    ethereumWalletAddress {
                        publicProfile {
                            profileName
                        }
                    }
                }
                mainMedia {
                    originalUrl
                }
                posterFrameMedia {
                    originalUrl
                }
            }
        `,
        nftItemDraftRef
    );
    const { creatorAddress, metadata } = nftItem || {};
    const { name: draftTitle, user, mainMedia, posterFrameMedia } = nftItemDraft || {};
    const profileName =
        creatorAddress?.publicProfile?.profileName ||
        user?.ethereumWalletAddress?.publicProfile?.profileName;

    const mediaUrl =
        metadata?.media?.posterFrameUrl ||
        metadata?.media?.webUrl ||
        posterFrameMedia?.originalUrl ||
        mainMedia?.originalUrl;

    let editionText: ReactNode;
    if (nftItem?.isERC1155) {
        editionText = (
            <FormattedMessage
                id="ActionModal.HeaderWithImage.edition1155"
                defaultMessage="Edition of {totalSupply}"
                values={{ totalSupply: nftItem?.totalSupply }}
            />
        );
    } else if (metadata?.name || metadata?.edition) {
        editionText = (
            <FormattedMessage
                id="ActionModal.HeaderWithImage.edition721"
                defaultMessage="edition: {edition}"
                values={{
                    edition: getEditionFromNftName(metadata?.name, metadata?.edition) || '1/1',
                }}
            />
        );
    } else {
        editionText = (
            <FormattedMessage
                id="ActionModal.HeaderWithImage.noEdition"
                defaultMessage="Edition not set"
            />
        );
    }

    return (
        <HeadingLevel>
            {Heading => (
                <div className={styles.headerWrapper}>
                    <div className={styles.titleWrapper}>
                        <div className={styles.header}>
                            {status === 'success' && <CircleCheck className={styles.icon} />}
                            {status === 'loading' && (
                                <Spinner size="tiny" black containerClass={styles.spinner} />
                            )}
                            <Heading className={styles.heading}>{title}</Heading>
                        </div>
                    </div>
                    <div className={styles.itemDetails}>
                        {mediaUrl ? (
                            <img
                                className={styles.itemImage}
                                src={addParams(mediaUrl, styles.MEDIA_WIDTH) || ''}
                                alt=""
                            />
                        ) : (
                            <div className={styles.emptyImageWrapper}>
                                <Image className={styles.emptyImageIcon} />
                            </div>
                        )}
                        <div className={styles.itemDetailsText}>
                            {profileName && <span>{profileName}</span>}
                            <span>{editionText}</span>
                            <strong>{metadata?.name || draftTitle}</strong>
                        </div>
                    </div>
                </div>
            )}
        </HeadingLevel>
    );
};
